/**
 * DecidA Waitlist Registration API
 *
 * Wraps POST /api/waitlist/ with:
 *   - AbortController timeout
 *   - Idempotent UX (200 = duplicate, 201 = new — both treated as success)
 *   - Normalised error responses for 400 (field errors) and 429 (rate limit)
 *
 * Setup:
 *   VITE_API_BASE_URL=http://localhost:8000   (no trailing slash)
 */

const API_BASE   = import.meta.env.VITE_API_BASE_URL ?? ''
const ENDPOINT   = `${API_BASE}/api/waitlist/`
const TIMEOUT_MS = 12_000

/* ─────────────────────────────────────────────────────────────
   Internal helpers
   ───────────────────────────────────────────────────────────── */

function flattenFieldErrors(errors) {
  return Object.fromEntries(
    Object.entries(errors).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? messages[0] : String(messages),
    ])
  )
}

const API_TO_FORM_FIELD = {
  name:          'name',
  email:         'email',
  phone:         'phone',
  area:          'area',
  interest_type: 'interestType',
  source:        'source',
}

function mapFieldErrors(apiErrors) {
  return Object.fromEntries(
    Object.entries(apiErrors).map(([apiKey, msg]) => [
      API_TO_FORM_FIELD[apiKey] ?? apiKey,
      msg,
    ])
  )
}

/* ─────────────────────────────────────────────────────────────
   Public API
   ───────────────────────────────────────────────────────────── */

/**
 * Register a buyer (or merchant-curious person) for the pre-launch waitlist.
 *
 * @param {object} payload — camelCase form values
 * @param {string} payload.name
 * @param {string} payload.email
 * @param {string} [payload.phone]
 * @param {string} [payload.area]
 * @param {'BUYER'|'MERCHANT'|'BOTH'} [payload.interestType='BUYER']
 * @param {string} [payload.source]
 * @returns {Promise<{ok: boolean, data?: object, alreadyRegistered?: boolean,
 *                    fieldErrors?: object, generalError?: string,
 *                    rateLimited?: boolean}>}
 */
export async function registerWaitlist(payload) {
  if (!API_BASE && import.meta.env.DEV) {
    console.warn(
      '[WaitlistAPI] VITE_API_BASE_URL is not set. ' +
      'Add it to your .env file, e.g. VITE_API_BASE_URL=http://localhost:8000'
    )
  }

  const body = {
    name:          payload.name,
    email:         payload.email,
    phone:         payload.phone || '',
    area:          payload.area || '',
    interest_type: payload.interestType || 'BUYER',
    source:        payload.source || '',
  }

  const controller = new AbortController()
  const timeoutId  = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const response = await fetch(ENDPOINT, {
      method:  'POST',
      signal:  controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    })

    clearTimeout(timeoutId)

    // 201 = new signup, 200 = duplicate email (idempotent — still success for UX)
    if (response.status === 201 || response.status === 200) {
      const data = await response.json().catch(() => ({}))
      return {
        ok: true,
        data,
        alreadyRegistered: response.status === 200,
      }
    }

    if (response.status === 400) {
      const json = await response.json().catch(() => ({}))
      const fieldErrors = mapFieldErrors(flattenFieldErrors(json.errors ?? {}))
      if (import.meta.env.DEV) {
        console.warn('[WaitlistAPI] 400 field errors:', fieldErrors)
      }
      return {
        ok: false,
        fieldErrors,
        generalError: Object.keys(fieldErrors).length
          ? 'Please fix the highlighted fields and try again.'
          : 'Your submission could not be processed. Please check your details.',
      }
    }

    if (response.status === 429) {
      return {
        ok: false,
        rateLimited: true,
        generalError: 'Too many submissions from your network. Please try again later.',
      }
    }

    if (import.meta.env.DEV) {
      const raw = await response.text().catch(() => '')
      console.error(`[WaitlistAPI] Unexpected ${response.status}:`, raw)
    }
    return {
      ok: false,
      generalError: 'An unexpected error occurred. Please try again shortly.',
    }

  } catch (err) {
    clearTimeout(timeoutId)

    if (import.meta.env.DEV) {
      console.error('[WaitlistAPI] Request error:', err)
    }

    if (err.name === 'AbortError') {
      return {
        ok: false,
        generalError: 'Request timed out. Please check your connection and try again.',
      }
    }

    return {
      ok: false,
      generalError: 'Network error. Please check your connection and try again.',
    }
  }
}
